import { useState } from 'react'
import LaunchCenter from '@/components/LaunchCenter'
import CalculatorDashboard from '@/components/CalculatorDashboard'
import KeywordDirectory from '@/components/KeywordDirectory'
import ContentBrief from '@/components/ContentBrief'
import type { KeywordRow } from '@/data/keywords'

// ---------------------------------------------------------------------------
// Navigation config
// ---------------------------------------------------------------------------
const NAV_LINKS = [
  { id: 'overview',     label: 'Overview'           },
  { id: 'calculators',  label: 'Calculators'        },
  { id: 'keywords',     label: 'Keyword Directory'  },
]

// ---------------------------------------------------------------------------
// Smooth-scroll helper — compensates for sticky header height (~57 px)
// ---------------------------------------------------------------------------
function scrollTo(id: string) {
  const el = document.getElementById(id)
  if (!el) return
  const offset = 64
  const top = el.getBoundingClientRect().top + window.scrollY - offset
  window.scrollTo({ top, behavior: 'smooth' })
}

// ---------------------------------------------------------------------------
// Home
// ---------------------------------------------------------------------------
export default function Home() {
  const [selected, setSelected] = useState<KeywordRow | null>(null)

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 antialiased">

      {/* ─── Sticky dark-slate header ─────────────────────────────────────── */}
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-900">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">

          {/* Logo / wordmark */}
          <button
            type="button"
            onClick={() => scrollTo('overview')}
            className="flex shrink-0 items-center gap-2.5 text-left"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-400 font-mono text-sm font-bold text-slate-900 select-none">
              JC
            </span>
            <span className="leading-tight">
              <span className="block text-sm font-semibold tracking-tight text-white">
                Junction Calculator Hub
              </span>
              <span className="block font-mono text-[11px] text-slate-400">
                junctioncalculator.com
              </span>
            </span>
          </button>

          {/* Centre nav — hidden on mobile */}
          <nav className="hidden items-center gap-0.5 md:flex">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => scrollTo(link.id)}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA */}
          <button
            type="button"
            onClick={() => scrollTo('calculators')}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-white px-3.5 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
          >
            <span className="hidden sm:inline">Open</span> Tools
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M2.5 7h9M7.5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </header>

      {/* ─── Page body ────────────────────────────────────────────────────── */}
      <main>

        {/* Anchor for Overview / Launch Center — sits flush above the section */}
        <div id="overview" />

        {/* ── Component A: Domain Launch Command Center ───────────────────── */}
        <LaunchCenter />

        {/* Section divider */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <hr className="mt-16 border-slate-200" />
        </div>

        {/* ── Component D: Live Calculator Sandboxes ──────────────────────── */}
        {/*    CalculatorDashboard owns id="calculators" on its <section>      */}
        <CalculatorDashboard />

        {/* Section divider */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <hr className="mt-16 border-slate-200" />
        </div>

        {/* ── Component B: Vetted 50-Keyword Directory ────────────────────── */}
        {/*    KeywordDirectory owns id="keywords" on its <section>            */}
        {/*    onSelect wires into selected state — ready for Component C      */}
        <KeywordDirectory
          selectedId={selected?.id}
          onSelect={setSelected}
        />

        {/* ── Component C: AI Content Brief Generator ────────────────────── */}
        {selected ? (
          <ContentBrief
            keyword={selected}
            onClear={() => setSelected(null)}
          />
        ) : null}

        {/* Bottom breathing room */}
        <div className="pb-24" />
      </main>

      {/* ─── Footer ───────────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-900 font-mono text-xs font-bold text-emerald-400 select-none">
                JC
              </span>
              <div>
                <span className="block font-mono text-sm font-semibold text-slate-800">
                  junctioncalculator.com
                </span>
                <span className="block text-xs text-slate-400">
                  Multi-niche programmatic calculator portal
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
              <span>NEC 314.16</span>
              <span>AASHTO Green Book</span>
              <span>IPC Thermal</span>
              <span>NMRA Turnout Geometry</span>
              <span>Crane TP-410</span>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => scrollTo(link.id)}
                className="rounded-md px-2 py-1 text-xs text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              >
                {link.label}
              </button>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-400">
            Mathematical results are provided for reference purposes only. Always verify engineering calculations
            against the latest adopted code edition and applicable professional standards.
          </p>
        </div>
      </footer>

    </div>
  )
}
